import { useEffect, useMemo } from "react";

const DEFAULT_KEYWORDS =
  "IB calculator, IB grade calculator, IB predictor, IB grade boundaries, IB diploma score calculator, IB tutors, ibcalculator";
const DEFAULT_ROBOTS =
  "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";
const STRUCTURED_DATA_SCRIPT_ID = "dynamic-seo-structured-data";

/**
 * Manages per-page <head> meta tags for SEO.
 * During pre-rendering, Puppeteer captures the final DOM state,
 * so these changes end up in the static HTML files.
 */
export default function SEO({
  title,
  description,
  path,
  keywords = DEFAULT_KEYWORDS,
  robots = DEFAULT_ROBOTS,
  structuredData = null,
  type = "website",
  publishedTime,
}) {
  const structuredDataJson = useMemo(
    () => (structuredData ? JSON.stringify(structuredData) : ""),
    [structuredData]
  );

  useEffect(() => {
    const pathname = path || window.location.pathname;
    const url = `https://www.ibcalculator.com${pathname === "/" ? "" : pathname}`;

    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', title);
      setMeta('meta[name="twitter:title"]', title);
    }

    if (description) {
      setMeta('meta[name="description"]', description);
      setMeta('meta[property="og:description"]', description);
      setMeta('meta[name="twitter:description"]', description);
    }

    setMeta('meta[name="keywords"]', keywords);
    setMeta('meta[name="robots"]', robots);
    setAttr('link[rel="canonical"]', "href", url);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:type"]', type);
    syncPublishedTime(publishedTime);
    syncStructuredData(structuredDataJson);
  }, [title, description, keywords, path, robots, structuredDataJson, type, publishedTime]);

  return null;
}

function setMeta(selector, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", value);
}

function setAttr(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

function syncPublishedTime(value) {
  const existing = document.querySelector('meta[property="article:published_time"]');
  if (!value) {
    existing?.remove();
    return;
  }
  const meta = existing || document.createElement("meta");
  meta.setAttribute("property", "article:published_time");
  meta.setAttribute("content", value);
  if (!existing) document.head.appendChild(meta);
}

function syncStructuredData(structuredDataJson) {
  const existing = document.getElementById(STRUCTURED_DATA_SCRIPT_ID);

  if (!structuredDataJson) {
    existing?.remove();
    return;
  }

  document
    .querySelectorAll(`script[type="application/ld+json"]:not(#${STRUCTURED_DATA_SCRIPT_ID})`)
    .forEach((script) => script.remove());

  const script =
    existing ||
    Object.assign(document.createElement("script"), {
      id: STRUCTURED_DATA_SCRIPT_ID,
      type: "application/ld+json",
    });

  script.textContent = structuredDataJson;

  if (!existing) {
    document.head.appendChild(script);
  }
}
